import { Flex } from "antd";
import { get } from "lodash";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { TUploadedFile } from "~/client/http/FileController";
import NewSave from "~/components/Icons/NewSave";
import NewSend from "~/components/Icons/NewSend";
import Preloader from "~/components/Preloader";
import { useAppeals, useContactsSelectOptions } from "~/entity/Appeals";
import { IAppealSchema, TAppealFilter } from "~/entity/Appeals/model/schema";
import { useSetLocationData } from "~/hooks/locationData";
import { useURLParamsGetter } from "~/hooks/useURLParamsSetter";
import { toServer } from "~/pages/Appeal/model/filterAdapter";
import { CommentWrapper } from "~/shared/ui/CommentWrapper";
import Modal from "~/shared/ui/Fields/Modal";
import RedesignButton from "~/shared/ui/Fields/RedesignButton";
import { FormGen } from "~/widgets/FormGen";
import { getDefaultValues } from "~/widgets/Modals/AddAppeal/model/adapter";
import { IAppealSendForm } from "~/widgets/Modals/AddAppeal/model/schema";
import { getFormConfigToSendAppeal } from "../model/config";
import { AppealStateFormIProps } from "~/pages/Appeal/ui/Appeal";
import s from "./AddAppeal.less";

const defaultValues = {
  contactPersonId: { label: "Выберете контактное лицо", value: null },
  comment: "",
  fileIds: [],
};

interface IAddAppealProps {
  scope: "appeal" | "resolution" | "editor";
  isOpenModal: boolean;
  isCloseAppealByAdmin?: boolean;
  onSubmit: (
    value: IAppealSendForm & { submitType: "draft" | "sent" },
    appeal: IAppealSchema
  ) => void;
  getDataConfirmation: (data: AppealStateFormIProps) => void;
}

export const AddAppeal = ({
  scope,
  isOpenModal,
  isCloseAppealByAdmin,
  onSubmit,
  getDataConfirmation,
}: IAddAppealProps) => {
  const { t } = useTranslation();
  const setData = useSetLocationData();
  const { currentId, ...settedFilter } = useURLParamsGetter<
    { currentId: string } & TAppealFilter
  >();

  const { dealerGroup } = toServer(settedFilter);
  const { queryKeys, getAppealById } = useAppeals();
  const { contactsSelectOptions, isLoading: isLoadingContacts } =
    useContactsSelectOptions({ isEnabled: isOpenModal, filter: dealerGroup });

  const appealQuery = useQuery({
    queryKey: queryKeys.byId(currentId),
    queryFn: () => getAppealById(currentId),
    enabled: !!isOpenModal && !!currentId,
  });

  const appealInstance = get(appealQuery.data, "data.appeal", null);

  const getUploadedFiles = (): TUploadedFile[] => {
    if (appealInstance && appealInstance.files) {
      let targetScope: string;

      if (scope === "editor") {
        targetScope = "resolution";
      } else {
        targetScope = "appeal";
      }

      return appealInstance.files
        .filter((file) => file.scope === targetScope)
        .map((file) => ({
          id: String(file.fileId),
          name: file.name || file.fileName || "Файл",
          link: file.link,
          mimeType: file.mimeType,
          createdAt: new Date(file.createdAt),
          path: file.link,
          UserId: "",
        }));
    }
    return [];
  };

  const currentDefaultValues = getDefaultValues({
    scope,
    appealInstance,
    contactsSelectOptions,
  });
  const fileList = getUploadedFiles();

  const onAddModalClose = () => {
    setData("openModal", "");
  };

  const config = getFormConfigToSendAppeal({
    scope,
    translateFunc: t,
    contactsSelectOptionsList: contactsSelectOptions,
    fileFieldWrapperClassName: s.fileFieldWrapper,
    fileList,
  });

  const [sendType, setSendType] = useState<"draft" | "sent">(null);

  const handleSubmitClickDecorator = (type: "draft" | "sent") => () =>
    setSendType(type);
  const submitDecorator = (data: IAppealSendForm) => {
    if (scope === "resolution") {
      getDataConfirmation({
        value: { ...data, submitType: sendType },
        appeal: appealInstance,
      });
    } else onSubmit({ ...data, submitType: sendType }, appealInstance);
  };

  const submitSlot = (onDefaultReset: () => void) => (
    <Flex gap={8} style={{ width: 600 }}>
      <RedesignButton
        theme="main"
        txtBtn={"Отмена"}
        onClick={() => {
          onDefaultReset();
          onAddModalClose();
        }}
      />
      {!isCloseAppealByAdmin && scope !== "editor" && (
        <RedesignButton
          theme="clear-accent"
          isForm
          txtBtn={"Сохранить"}
          onClick={handleSubmitClickDecorator("draft")}
          iconComponent={<NewSave />}
        />
      )}
      <RedesignButton
        theme="accent"
        isForm
        txtBtn={"Отправить"}
        onClick={handleSubmitClickDecorator("sent")}
        iconComponent={<NewSend />}
      />
    </Flex>
  );

  return (
    <Modal
      isOpen={isOpenModal}
      title={
        isCloseAppealByAdmin
          ? "Решение по апелляции"
          : scope === "editor"
          ? "Изменение решения по апелляции"
          : "Подать апелляцию"
      }
      actionClose={onAddModalClose}
    >
      <Flex vertical gap={24} style={{ width: "100%" }} align={"start"}>
        {!isCloseAppealByAdmin && appealInstance?.needInfoComment && (
          <Flex style={{ width: "100%" }} gap={12} vertical>
            <CommentWrapper
              title="Запрос доп. информации:"
              comment={appealInstance.needInfoComment}
            />
            <span
              style={{ width: "100%", height: "1px", background: "#adb7bd" }}
            />
          </Flex>
        )}
        {appealQuery.isLoading || isLoadingContacts ? (
          <Preloader />
        ) : (
          <>
            <FormGen<IAppealSendForm>
              config={config}
              defaultValues={currentDefaultValues}
              onSubmit={submitDecorator}
              submitSlot={({ onDefaultReset }) => submitSlot(onDefaultReset)}
            />
            <span className={s.rule}>* - обязательное поле</span>
          </>
        )}
      </Flex>
    </Modal>
  );
};
